import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
} from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/cn'

export interface Select2Option<T extends string | number = string> {
  value: T
  label: string
  description?: string
  /** Optional emoji / flag / short prefix shown before the label. */
  icon?: string
  disabled?: boolean
}

export interface Select2Props<T extends string | number = string> {
  options: Select2Option<T>[]
  value: T | null
  onChange: (value: T) => void
  placeholder?: string
  searchable?: boolean
  disabled?: boolean
  className?: string
  emptyMessage?: string
  /** Dropdown opens below (default) or above the trigger. */
  placement?: 'bottom' | 'top'
  'aria-label'?: string
}

/**
 * Institutional searchable select — keyboard nav, typeahead, portal dropdown.
 */
export function Select2<T extends string | number = string>({
  options,
  value,
  onChange,
  placeholder,
  searchable = true,
  disabled = false,
  className,
  emptyMessage,
  placement = 'bottom',
  'aria-label': ariaLabel,
}: Select2Props<T>) {
  const { t } = useTranslation('common')
  const listId = useId()
  const rootRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [activeIndex, setActiveIndex] = useState(0)
  const [menuStyle, setMenuStyle] = useState<CSSProperties>({})

  const selected = useMemo(
    () => options.find((o) => o.value === value) ?? null,
    [options, value],
  )

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return options
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(q) ||
        o.description?.toLowerCase().includes(q) ||
        String(o.value).toLowerCase().includes(q),
    )
  }, [options, query])

  const updateMenuPosition = useCallback(() => {
    const el = rootRef.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    const gap = 4
    const maxH = 224
    const style: CSSProperties = {
      position: 'fixed',
      left: rect.left,
      width: Math.max(rect.width, 140),
      zIndex: 100,
    }
    if (placement === 'top') {
      style.bottom = window.innerHeight - rect.top + gap
      style.maxHeight = Math.min(maxH, rect.top - gap - 8)
    } else {
      style.top = rect.bottom + gap
      style.maxHeight = Math.min(maxH, window.innerHeight - rect.bottom - gap - 8)
    }
    setMenuStyle(style)
  }, [placement])

  useLayoutEffect(() => {
    if (!open) return
    updateMenuPosition()
  }, [open, updateMenuPosition, filtered.length])

  useEffect(() => {
    if (!open) return
    const onWin = () => updateMenuPosition()
    window.addEventListener('resize', onWin)
    window.addEventListener('scroll', onWin, true)
    return () => {
      window.removeEventListener('resize', onWin)
      window.removeEventListener('scroll', onWin, true)
    }
  }, [open, updateMenuPosition])

  useEffect(() => {
    if (!open) return
    setQuery('')
    const idx = Math.max(
      0,
      filtered.findIndex((o) => o.value === value),
    )
    setActiveIndex(idx)
    requestAnimationFrame(() => searchRef.current?.focus())
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      const target = e.target as Node
      if (rootRef.current?.contains(target)) return
      if (menuRef.current?.contains(target)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  const selectAt = useCallback(
    (index: number) => {
      const opt = filtered[index]
      if (!opt || opt.disabled) return
      onChange(opt.value)
      setOpen(false)
    },
    [filtered, onChange],
  )

  const onKeyDown = (e: KeyboardEvent) => {
    if (disabled) return
    if (!open && (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown')) {
      e.preventDefault()
      setOpen(true)
      return
    }
    if (!open) return

    if (e.key === 'Escape') {
      e.preventDefault()
      setOpen(false)
    } else if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIndex((i) => Math.min(filtered.length - 1, i + 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIndex((i) => Math.max(0, i - 1))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      selectAt(activeIndex)
    }
  }

  const menu = open
    ? createPortal(
        <div
          ref={menuRef}
          style={menuStyle}
          className={cn(
            'overflow-hidden rounded border border-terminal-border',
            'bg-terminal-surface shadow-2xl',
          )}
        >
          {searchable ? (
            <div className="border-b border-terminal-border p-1.5">
              <input
                ref={searchRef}
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value)
                  setActiveIndex(0)
                }}
                placeholder={t('actions.search')}
                className={cn(
                  'h-8 w-full rounded border border-terminal-border bg-terminal-bg px-2 text-sm',
                  'text-terminal-fg placeholder:text-terminal-muted',
                  'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-accent',
                )}
              />
            </div>
          ) : null}

          <ul
            id={listId}
            role="listbox"
            className="terminal-scroll overflow-y-auto py-1"
            style={{ maxHeight: menuStyle.maxHeight }}
          >
            {filtered.length === 0 ? (
              <li className="px-3 py-2 text-xs text-terminal-muted">
                {emptyMessage ?? t('actions.noResults')}
              </li>
            ) : (
              filtered.map((opt, index) => {
                const active = index === activeIndex
                const isSelected = opt.value === value
                return (
                  <li
                    key={String(opt.value)}
                    role="option"
                    aria-selected={isSelected}
                  >
                    <button
                      type="button"
                      disabled={opt.disabled}
                      onMouseEnter={() => setActiveIndex(index)}
                      onClick={() => selectAt(index)}
                      className={cn(
                        'flex w-full items-start gap-2 px-3 py-1.5 text-start text-sm',
                        active && 'bg-terminal-elevated',
                        isSelected && 'text-accent',
                        opt.disabled && 'cursor-not-allowed opacity-40',
                      )}
                    >
                      {opt.icon ? (
                        <span
                          className="mt-0.5 shrink-0 text-base leading-none"
                          aria-hidden
                        >
                          {opt.icon}
                        </span>
                      ) : null}
                      <span className="min-w-0 flex-1">
                        <span className="block font-medium">{opt.label}</span>
                        {opt.description ? (
                          <span className="block text-[11px] text-terminal-muted">
                            {opt.description}
                          </span>
                        ) : null}
                      </span>
                    </button>
                  </li>
                )
              })
            )}
          </ul>
        </div>,
        document.body,
      )
    : null

  return (
    <div ref={rootRef} className={cn('relative', className)} onKeyDown={onKeyDown}>
      <button
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-label={ariaLabel}
        onClick={() => setOpen((o) => !o)}
        className={cn(
          'flex h-9 w-full items-center justify-between gap-2 rounded border px-2.5 text-start text-sm',
          'border-terminal-border bg-terminal-bg text-terminal-fg',
          'hover:border-terminal-muted focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-accent',
          disabled && 'cursor-not-allowed opacity-50',
        )}
      >
        <span
          className={cn(
            'flex min-w-0 items-center gap-2 truncate',
            !selected && 'text-terminal-muted',
          )}
        >
          {selected?.icon ? (
            <span className="shrink-0 text-base leading-none" aria-hidden>
              {selected.icon}
            </span>
          ) : null}
          <span className="truncate">
            {selected?.label ?? placeholder ?? t('actions.select')}
          </span>
        </span>
        <ChevronIcon open={open} />
      </button>
      {menu}
    </div>
  )
}

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      viewBox="0 0 16 16"
      className={cn(
        'h-3.5 w-3.5 shrink-0 text-terminal-muted transition-transform',
        open && 'rotate-180',
      )}
      aria-hidden
    >
      <path
        d="M4 6l4 4 4-4"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}
